/**
 * ConnectorSyncZone Component
 *
 * Replaces the VersionUploadZone for tables with automated connectors.
 * Shows a "Sync Now" button for pull-based connectors (http_fetch, sql, nango, hubspot_direct)
 * and an informational message for push-based connectors (post_api, email).
 *
 * @module features/workspace/components/PreviewPanel/ConnectorSyncZone
 */

import { RefreshCw, Loader2, Info } from 'lucide-react';
import type { ConnectorType } from '@loader/shared-types';
import { Button } from '@loader/ui';

import { CONNECTOR_META } from '@/features/inputs/types';
import { useTriggerCustomSync } from '@/features/inputs/hooks/useCustomConnectors';
import { useTriggerNangoSync } from '@/features/inputs/hooks/useNangoConnectors';
import { toast } from '@/lib/toast';

const SYNCABLE_TYPES: ConnectorType[] = ['http_fetch', 'sql', 'nango', 'hubspot_direct'];

interface ConnectorSyncZoneProps {
  tableId: string;
  connectorType: ConnectorType;
  /** Called after sync is successfully triggered so parent can start polling */
  onSyncTriggered?: () => void;
}

export function ConnectorSyncZone({ tableId, connectorType, onSyncTriggered }: ConnectorSyncZoneProps) {
  const triggerCustomSync = useTriggerCustomSync();
  const triggerNangoSync = useTriggerNangoSync();

  const isSyncable = SYNCABLE_TYPES.includes(connectorType);
  const isSyncing = triggerCustomSync.isPending || triggerNangoSync.isPending;

  const meta = CONNECTOR_META[connectorType];
  const Icon = meta?.icon;

  const handleSync = () => {
    const callbacks = {
      onSuccess: () => {
        toast.success('Sync triggered');
        onSyncTriggered?.();
      },
      onError: () => toast.error('Failed to trigger sync'),
    };

    if (connectorType === 'nango') {
      triggerNangoSync.mutate(tableId, callbacks);
    } else {
      triggerCustomSync.mutate(tableId, callbacks);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border border-dashed p-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {meta?.logo ? (
          <img src={meta.logo} alt="" className="size-4 shrink-0 rounded-full object-contain" />
        ) : (
          Icon && <Icon className="size-4 shrink-0" />
        )}
        <span>{meta?.label ?? connectorType}</span>
      </div>

      {isSyncable ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={isSyncing}
          className="h-7 gap-1.5 text-xs"
        >
          {isSyncing ? (
            <>
              <Loader2 className="size-3 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="size-3" />
              Sync Now
            </>
          )}
        </Button>
      ) : (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Info className="size-3" />
          Waiting for data
        </span>
      )}
    </div>
  );
}
